export type ParticipantAppointmentWriteOutcome =
  'not_applied' | 'applied' | 'ambiguous';

export interface ParticipantAppointmentErrorEnvelope {
  contractVersion: 'appointment-participant-error.v1';
  error: string;
  retryAfterSeconds: number | null;
  writeOutcome: ParticipantAppointmentWriteOutcome;
}

const AMBIGUOUS_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504]);
const AMBIGUOUS_CLIENT_STATUSES = new Set([
  'FETCH_ERROR',
  'PARSING_ERROR',
  'TIMEOUT_ERROR',
]);

const TRUSTED_APPOINTMENT_ERROR_CODES = new Set([
  'appointment_client_upgrade_required',
  'appointment_conflict',
  'appointment_contract_frozen',
  'appointment_forbidden',
  'appointment_not_found',
  'appointment_request_invalid',
  'appointment_series_fence_required',
  'appointment_upstream_credentials_invalid',
  'appointment_upstream_declined',
  'appointment_upstream_unavailable',
  'appointment_write_ambiguous',
  'calendar_event_title_stale',
]);

const TRUSTED_PLATFORM_ADMIN_APPOINTMENT_ERROR_CODES = new Set([
  'admin_appointment_conflict',
  'admin_appointment_forbidden',
  'admin_appointment_not_found',
  'admin_appointment_rejected',
  'admin_appointment_request_invalid',
  'admin_appointment_result_truncated',
  'admin_appointment_terminal_hydration_forbidden',
  'admin_appointment_upstream_contract_invalid',
  'admin_appointment_write_ambiguous',
  'admin_calendar_upstream_not_ready',
  'admin_calendar_upstream_unauthorized',
  'admin_calendar_upstream_unavailable',
  'upstream_identity_mismatch',
]);

export interface TrustedAppointmentErrorEnvelope {
  code: string;
  error: string;
  write_outcome: 'not_applied' | 'not_applicable' | 'unknown';
}

export interface TrustedPlatformAdminAppointmentErrorEnvelope {
  code: string;
  error: string;
  write_outcome: 'not_applied' | 'not_applicable' | 'unknown';
}

const decodeTrustedErrorEnvelope = <
  TEnvelope extends
    | TrustedAppointmentErrorEnvelope
    | TrustedPlatformAdminAppointmentErrorEnvelope,
>(
  value: unknown,
  trustedCodes: Set<string>,
): TEnvelope | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const envelope = value as Record<string, unknown>;
  if (
    Object.keys(envelope).length !== 3 ||
    typeof envelope.error !== 'string' ||
    !envelope.error.trim() ||
    typeof envelope.code !== 'string' ||
    !trustedCodes.has(envelope.code) ||
    (envelope.write_outcome !== 'not_applied' &&
      envelope.write_outcome !== 'not_applicable' &&
      envelope.write_outcome !== 'unknown')
  ) {
    return undefined;
  }
  return envelope as unknown as TEnvelope;
};

export const decodeTrustedAppointmentErrorEnvelope = (
  value: unknown,
): TrustedAppointmentErrorEnvelope | undefined =>
  decodeTrustedErrorEnvelope(value, TRUSTED_APPOINTMENT_ERROR_CODES);

export const decodeTrustedPlatformAdminAppointmentErrorEnvelope = (
  value: unknown,
): TrustedPlatformAdminAppointmentErrorEnvelope | undefined =>
  decodeTrustedErrorEnvelope(
    value,
    TRUSTED_PLATFORM_ADMIN_APPOINTMENT_ERROR_CODES,
  );

const hasAmbiguousTransportStatus = (status: unknown): boolean =>
  status === undefined ||
  (typeof status === 'number' && AMBIGUOUS_HTTP_STATUSES.has(status)) ||
  (typeof status === 'string' && AMBIGUOUS_CLIENT_STATUSES.has(status));

export const shouldReconcileAppointmentWrite = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { data, status } = error as { data?: unknown; status?: unknown };
  const envelope = decodeTrustedAppointmentErrorEnvelope(data);
  if (envelope) {
    return (
      envelope.write_outcome !== 'not_applied' &&
      envelope.write_outcome !== 'not_applicable'
    );
  }
  return hasAmbiguousTransportStatus(status);
};

export const shouldReconcilePlatformAdminAppointmentWrite = (
  error: unknown,
): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { data, status } = error as { data?: unknown; status?: unknown };
  const envelope = decodeTrustedPlatformAdminAppointmentErrorEnvelope(data);
  return envelope
    ? envelope.write_outcome === 'unknown'
    : hasAmbiguousTransportStatus(status);
};

export const decodeParticipantAppointmentErrorEnvelope = (
  value: unknown,
): ParticipantAppointmentErrorEnvelope => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid participant error envelope');
  }
  const envelope = value as Record<string, unknown>;
  const keys = Object.keys(envelope).sort().join(',');
  const retryAfterSeconds = envelope.retry_after_seconds;
  if (
    keys !== 'contract_version,error,retry_after_seconds,write_outcome' ||
    envelope.contract_version !== 'appointment-participant-error.v1' ||
    typeof envelope.error !== 'string' ||
    envelope.error.length === 0 ||
    envelope.error.length > 100 ||
    (envelope.write_outcome !== 'not_applied' &&
      envelope.write_outcome !== 'applied' &&
      envelope.write_outcome !== 'ambiguous') ||
    (retryAfterSeconds !== null &&
      (!Number.isInteger(retryAfterSeconds) ||
        (retryAfterSeconds as number) < 0))
  ) {
    throw new Error('Invalid participant error envelope');
  }
  return {
    contractVersion: 'appointment-participant-error.v1',
    error: envelope.error,
    retryAfterSeconds: retryAfterSeconds as number | null,
    writeOutcome: envelope.write_outcome,
  };
};

export const shouldReconcileParticipantAppointmentWrite = (
  error: unknown,
): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { data, status } = error as { data?: unknown; status?: unknown };
  try {
    return (
      decodeParticipantAppointmentErrorEnvelope(data).writeOutcome !==
      'not_applied'
    );
  } catch {
    // The Moments bearer transport reports a private-state boundary even when
    // the boundary is crossed after a write's response arrived.
    return (
      status === 'MOMENTS_PRIVATE_BOUNDARY' ||
      hasAmbiguousTransportStatus(status)
    );
  }
};
